import { useAPIKeys } from 'features/settings'
import { Button, Typography } from 'procyon-ui'
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { enqueueNotification } from 'Utils/Helpers'
import StepContent from './Components/StepsContent'

export default function WizardModal({ open, onClose, onFinish }) {
  const [step, setStep] = useState(0)
  const [clusterName, setClusterName] = useState('')
  const [hostIP, setHostIP] = useState('')
  const [proxyType, setProxyType] = useState('PrivateProxy')
  const [isChecked, setIsChecked] = useState(false)
  const [selectedOS, setSelectedOS] = useState('')
  const [showInstructionModal, setShowInstructionModal] = useState(false)
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const { apiKeys } = useAPIKeys()
  const dialogRef = useRef(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setStep(0)
      setClusterName('')
      setHostIP('')
      setProxyType('PrivateProxy')
      setIsChecked(false)
      setSelectedOS('')
    }
  }, [open])

  const total = 5
  const canPrev = step > 0
  const isLast = step === total - 1

  // Validation: Step 2 is mandatory (proxyType, clusterName, hostIP)
  const isStep2Valid = useCallback(() => {
    const hasProxyType = Boolean(proxyType)
    const hasClusterName = Boolean(String(clusterName).trim())
    const hasHostIP = Boolean(String(hostIP).trim())
    const hasError = proxyType === 'Relay' ? false : Boolean(error)

    // valid only when we have all required fields AND there is NO error
    return hasProxyType && hasClusterName && hasHostIP && !hasError
  }, [proxyType, clusterName, hostIP, error])

  const next = useCallback(() => {
    // Block moving past Step 2 until required fields are filled
    if (step === 2 && !isStep2Valid()) {
      enqueueNotification(
        'Please select Private Proxy or Relay and fill Cluster Name and Host IP to continue.',
        'warning'
      )
      return
    }
    setStep((s) => Math.min(total - 1, s + 1))
  }, [step, isStep2Valid])
  const prev = useCallback(() => setStep((s) => Math.max(0, s - 1)), [])

  const handleFinish = useCallback(() => {
    onClose()
  }, [onFinish, clusterName, hostIP, proxyType, isChecked, selectedOS])

  const stepLabels = ['Create VM', 'Setup Docker', 'Gateway Type', 'Setup Config', 'Setup Agent']

  if (!open) return null

  return (
    <div className='fixed inset-0 z-[1000] flex items-center justify-center'>
      <div className='absolute inset-0 bg-black/50 backdrop-blur-[1px]' onClick={onClose} />
      <div
        ref={dialogRef}
        className='relative z-[1001] w-[min(96vw,720px)] rounded-[5px] max-h-[100vh] overflow-hidden bg-[var(--color-common-bg-base)] shadow-2xl ring-1 ring-black/5'
      >
        {/* Header */}
        <div className='flex items-start justify-between border-b border-gray-100 px-6 py-4'>
          <div>
            <Typography variant='h3'>Setup Gateways</Typography>
          </div>
          <Button variant='gray' onClick={onClose}>
            Close
          </Button>
        </div>

        {/* Breadcrumb Stepper */}
        <div className='flex flex-wrap items-center gap-2 border-b border-gray-100 px-6 py-3'>
          {stepLabels.map((label, i) => (
            <div key={label}>
              <button
                type='button'
                onClick={() => {
                  // Prevent jumping to steps after 2 unless Step 2 is valid
                  if (i > 2 && !isStep2Valid()) {
                    enqueueNotification(
                      `Please provide the cluster name and ${
                        proxyType === 'Relay' ? 'host name' : 'host IP'
                      }.`,
                      'warning'
                    )
                    return
                  }
                  setStep(i)
                }}
                className={[
                  'text-sm transition-colors',
                  i === step
                    ? 'text-[var(--color-font-primary)] font-semibold'
                    : i < step
                      ? 'text-gray-700 hover:text-gray-900'
                      : 'text-gray-400 hover:text-gray-600'
                ].join(' ')}
              >
                {label}
              </button>
              {i < stepLabels.length - 1 && <span className='mx-1 text-gray-300'>›</span>}
            </div>
          ))}
        </div>

        <div className='px-6 py-5 space-y-5'>
          <StepContent
            step={step}
            clusterName={clusterName}
            setClusterName={setClusterName}
            hostIP={hostIP}
            setHostIP={setHostIP}
            proxyType={proxyType}
            setProxyType={setProxyType}
            isChecked={isChecked}
            setIsChecked={setIsChecked}
            user={user}
            apiKeys={apiKeys}
            selectedOS={selectedOS}
            setSelectedOS={setSelectedOS}
            showInstructionModal={showInstructionModal}
            setShowInstructionModal={setShowInstructionModal}
            error={error}
            setError={setError}
          />
        </div>

        <div className='flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4'>
          <Button variant='gray' onClick={prev} disabled={!step}>
            Previous
          </Button>

          <div className='flex gap-4'>
            <Button
              variant='primary'
              onClick={isLast ? handleFinish : next}
              disabled={step === 2 && !isStep2Valid()}
            >
              {isLast ? 'Finish' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
